import { useContext, useEffect, useState } from "react";
import { BreakpointContext } from "./context";
import type { Breakpoint } from "./types";

export interface UseBreakpointResult {
  current: Breakpoint;
  below: (bp: Breakpoint) => boolean;
  atLeast: (bp: Breakpoint) => boolean;
}

/**
 * Responsive breakpoint hook matching Tailwind's breakpoint system.
 * Checks for BreakpointProvider override first, falls back to viewport detection.
 *
 * @example
 * ```tsx
 * const { current, below, atLeast } = useBreakpoint();
 *
 * const isMobile = below('md');      // < 768px
 * const isDesktop = atLeast('lg');   // >= 1024px
 * ```
 */
export function useBreakpoint(): UseBreakpointResult {
  const override = useContext(BreakpointContext);
  const [detected, setDetected] = useState(detectBreakpoint);

  useEffect(() => {
    if (override) return;

    const handleResize = () => setDetected(detectBreakpoint());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [override]);

  const current = override ?? detected;
  const currentIndex = BREAKPOINT_ORDER.indexOf(current);

  const below = (bp: Breakpoint): boolean => currentIndex < BREAKPOINT_ORDER.indexOf(bp);
  const atLeast = (bp: Breakpoint): boolean => currentIndex >= BREAKPOINT_ORDER.indexOf(bp);

  return { current, below, atLeast };
}

/** Tailwind v4 default breakpoints (min-width values in px) */
const BREAKPOINTS: Record<Breakpoint, number> = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  "2xl": 1536,
};

const BREAKPOINT_ORDER: Breakpoint[] = ["sm", "md", "lg", "xl", "2xl"];

function detectBreakpoint(): Breakpoint {
  if (typeof window === "undefined") return "sm";
  const width = window.innerWidth;
  if (width >= BREAKPOINTS["2xl"]) return "2xl";
  if (width >= BREAKPOINTS.xl) return "xl";
  if (width >= BREAKPOINTS.lg) return "lg";
  if (width >= BREAKPOINTS.md) return "md";
  return "sm";
}
